import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import { finalize } from 'rxjs';
import { ApiMethod, AuthEndPoints } from '../../../helper/apiconstant';
import { Httpservice } from '../../../services/httpservice';
import { StoredProfileImageDetails } from '../../../services/manage-session';
import { SessionService } from '../../../services/session.service';
import { ServerTimeService } from '../../../services/server-time';
import { DashboardHeaderComponent } from '../../structure/header/header';
import { DashboardHeaderUser } from '../../structure/header/header.interfaces';
import { DashboardSidebarComponent } from '../../structure/sidebar/sidebar';
import { DEFAULT_DASHBOARD_USER } from '../../dashboard/dashboard.data';
import {
  MyProfileApiResponseValue,
  MyProfileSessionDetails,
} from './my-profile.interfaces';

@Component({
  selector: 'app-my-profile-page',
  standalone: true,
  imports: [CommonModule, DashboardSidebarComponent, DashboardHeaderComponent],
  templateUrl: './my-profile.html',
  styleUrl: './my-profile.scss',
})
export class MyProfilePage implements OnInit {
  @ViewChild('profileInput') profileInput?: ElementRef<HTMLInputElement>;

  protected readonly title = 'My Profile';
  protected readonly subtitle = 'Upload, update, and remove your profile image';

  protected headerUser: DashboardHeaderUser = {
    ...DEFAULT_DASHBOARD_USER,
    profileImageUrl: null,
  };

  protected sidebarCollapsed = false;
  protected mobileSidebarOpen = false;
  protected isLoadingProfile = false;
  protected isSubmitting = false;
  protected errorMessage = '';
  protected successMessage = '';
  protected selectedFile: File | null = null;
  protected selectedFileName = '';
  protected selectedPreviewUrl: string | null = null;
  protected profileDetails: MyProfileSessionDetails | null = null;
  protected currentProfileImage: StoredProfileImageDetails | null = null;

  constructor(
    private readonly httpSvc: Httpservice,
    private readonly appSessionSvc: SessionService,
    private readonly serverTimeSvc: ServerTimeService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.syncProfileState(this.appSessionSvc.getProfileDetails());
  }

  ngOnInit(): void {
    this.loadProfileDetails();
  }

  protected handleMenuToggle(): void {
    if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      return;
    }

    this.mobileSidebarOpen = !this.mobileSidebarOpen;
  }

  protected closeMobileSidebar(): void {
    this.mobileSidebarOpen = false;
  }

  protected openFilePicker(): void {
    this.profileInput?.nativeElement.click();
  }

  protected handleFileSelection(event: Event): void {
    this.errorMessage = '';

    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      this.errorMessage = 'Please select a valid image file';
      input.value = '';
      this.selectedFile = null;
      this.selectedFileName = '';
      this.selectedPreviewUrl = this.currentProfileImage?.uploaded_file ?? null;
      return;
    }

    this.selectedFile = file;
    this.selectedFileName = file.name;

    const reader = new FileReader();
    reader.onload = () => {
      this.selectedPreviewUrl = typeof reader.result === 'string' ? reader.result : null;
    };
    reader.readAsDataURL(file);
  }

  protected saveProfileImage(): void {
    if (this.currentProfileImage) {
      this.updateProfileImage();
      return;
    }

    this.uploadProfileImage();
  }

  protected uploadProfileImage(): void {
    this.submitProfileImage(AuthEndPoints.UPLOAD_PROFILE_IMAGE, ApiMethod.POST, 'Profile image uploaded successfully');
  }

  protected updateProfileImage(): void {
    this.submitProfileImage(AuthEndPoints.UPDATE_PROFILE_IMAGE, ApiMethod.PATCH, 'Profile image updated successfully');
  }

  protected deleteProfileImage(): void {
    if (!this.currentProfileImage) {
      return;
    }

    this.errorMessage = '';
    this.successMessage = '';
    this.isSubmitting = true;

    this.httpSvc
      .requestCall(AuthEndPoints.DELETE_PROFILE_IMAGE, ApiMethod.DELETE)
      .pipe(
        finalize(() => {
          this.isSubmitting = false;
        }),
      )
      .subscribe({
        next: (response) => {
          if (this.isProfileMutationSuccessful(response)) {
            this.selectedFile = null;
            this.selectedFileName = '';
            this.selectedPreviewUrl = null;
            this.syncProfileState(response?.value as MyProfileApiResponseValue | undefined);
            this.successMessage = 'Profile image deleted successfully';
            
            this.cdr.markForCheck();
            this.loadProfileDetails();
             
            return;
          }

          this.errorMessage = this.resolveMutationError(response);
        },
        error: (error) => {
          this.errorMessage = this.resolveMutationError(error?.error ?? error);
          this.cdr.markForCheck();
        },
      });
  }

  protected get profileImageButtonLabel(): string {
    return this.currentProfileImage ? 'Update Image' : 'Upload Image';
  }

  protected get canDeleteProfileImage(): boolean {
    return Boolean(this.currentProfileImage);
  }

  protected get displayProfileImageUrl(): string | null {
    return this.selectedPreviewUrl || this.currentProfileImage?.uploaded_file || null;
  }

  private submitProfileImage(endpoint: string, method: ApiMethod, successMessage: string): void {
    this.errorMessage = '';
    this.successMessage = '';

    if (!this.selectedFile) {
      this.errorMessage = 'Please choose an image first';
      return;
    }

    const payload = new FormData();
    payload.append('file', this.selectedFile);
    this.isSubmitting = true;

    this.httpSvc
      .requestCall(endpoint, method, payload)
      .pipe(
        finalize(() => {
          this.isSubmitting = false;
        }),
      )
      .subscribe({
        next: (response) => {
          if (this.isProfileMutationSuccessful(response)) {
            this.selectedFile = null;
            this.selectedFileName = '';
            this.syncProfileState(response?.value as MyProfileApiResponseValue | undefined);
            this.successMessage = successMessage;
            this.cdr.markForCheck();
            return;
          }

          this.errorMessage = this.resolveMutationError(response);
        },
        error: (error) => {
          this.errorMessage = this.resolveMutationError(error?.error ?? error);
           this.cdr.markForCheck();
        },
      });
  }

  private loadProfileDetails(): void {
    this.isLoadingProfile = true;
    const cachedProfile = this.appSessionSvc.getProfileDetails();

    this.httpSvc
      .requestCall(AuthEndPoints.GET_MY_PROFILE, ApiMethod.GET, '')
      .pipe(
        finalize(() => {
          this.isLoadingProfile = false;
        }),
      )
      .subscribe({
        next: (response) => {
          if (this.isProfileResponseSuccessful(response)) {
            this.syncProfileState(response?.value as MyProfileApiResponseValue | undefined);
            this.cdr.markForCheck();
            return;
          }

          this.syncProfileState(cachedProfile ?? null);
        },
        error: () => {
          this.syncProfileState(cachedProfile ?? null);
          this.cdr.markForCheck();
        },
      });
  }

  private syncProfileState(profile: MyProfileSessionDetails | MyProfileApiResponseValue | null | undefined): void {
    const normalized = this.normalizeProfileDetails(profile);
    this.profileDetails = normalized;
    this.currentProfileImage = normalized?.profile_image ?? null;
    this.appSessionSvc.setProfileDetails(normalized);
    this.headerUser = this.buildHeaderUser(normalized);

    if (!this.selectedFile) {
      this.selectedPreviewUrl = normalized?.profile_image?.uploaded_file ?? null;
    }
  }

  private normalizeProfileDetails(
    profile: MyProfileSessionDetails | MyProfileApiResponseValue | null | undefined,
  ): MyProfileSessionDetails | null {
    if (!profile) {
      return null;
    }

    const current = this.profileDetails;
    const userId = this.resolveString((profile as MyProfileApiResponseValue).user_id ?? current?.user_id);
    const username = this.resolveString(profile.username ?? current?.username);
    const userFullName = this.resolveString(profile.user_fullname ?? current?.user_fullname);
    const email = this.resolveString(profile.email ?? current?.email);
    const mobileNo = this.resolveString(profile.mobile_no ?? current?.mobile_no);
    const roleName = this.resolveString(profile.role_name ?? current?.role_name);
    const roleDisplayName = this.resolveString(profile.role_display_name ?? current?.role_display_name);
    const profileImage = 'profile_image' in profile ? profile.profile_image : current?.profile_image ?? null;


    if (!username && !userFullName && !email && !mobileNo && !roleName && !roleDisplayName && !profileImage) {
      return null;
    }

    return {
      user_id: userId,
      username,
      user_fullname: userFullName,
      email,
      mobile_no: mobileNo,
      role_name: roleName,
      role_display_name: roleDisplayName,
      profile_image: profileImage
        ? {
            id: typeof profileImage.id === 'number' ? profileImage.id : null,
            filename: profileImage.filename ?? null,
            filepath: profileImage.filepath ?? null,
            uploaded_file: profileImage.uploaded_file ?? null,
          }
        : null,
    };
  }

  private buildHeaderUser(profile: MyProfileSessionDetails | null): DashboardHeaderUser {
    const fullName = profile?.user_fullname ?? DEFAULT_DASHBOARD_USER.fullName;
    return {
      ...DEFAULT_DASHBOARD_USER,
      fullName,
      initials: this.buildInitials(fullName),
      roleLabel: profile?.role_display_name ?? profile?.role_name ?? DEFAULT_DASHBOARD_USER.roleLabel,
      profileImageUrl: profile?.profile_image?.uploaded_file ?? null,
    };
  }

  private buildInitials(fullName: string): string {
    const parts = fullName
      .split(' ')
      .map((part) => part.trim())
      .filter(Boolean);

    if (!parts.length) {
      return DEFAULT_DASHBOARD_USER.initials;
    }

    return parts
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('');
  }

  private isProfileResponseSuccessful(response: any): boolean {
    return response?.status === 'TRUE' && response?.value;
  }

  private isProfileMutationSuccessful(response: any): boolean {
    return response?.status === 'TRUE' && response?.value;
  }

  private resolveString(value: unknown): string | null {
    return typeof value === 'string' && value.trim() ? value.trim() : null;
  }

  private resolveMutationError(response: any): string {
    const message = typeof response?.msg === 'string' ? response.msg : '';
    return message || 'Unable to update profile image';
  }

  protected get currentTimeLabel(): string {
    return this.serverTimeSvc.getCurrentTimeLabel();
  }
}
